import { defineComponent as _defineComponent } from 'vue'
import {
    computed,
    ref,
    reactive,
    onBeforeUnmount,
    onMounted,
    watch,
  } from "vue";
  import {
    getMasterplanLotStatusClass,
    getMasterplanLotStatusLabel,
    getMasterplanStatusBadgeStyle,
  } from "@/v2/helpers/masterplanLotStatus";
  import { calculatePolygonCentroid } from "@/helpers/lotNumber/lotCentroidCalculator";
  import { formatDate } from "@/helpers/date";
  import { getDocumentName } from "@/helpers/documents";
  import { LotStatus } from "@/enums/LotStatus";
  import {
    Bed,
    Bath,
    Car,
    Sofa,
    Maximize2,
    MoveHorizontal,
    Send,
    Layers,
    Navigation,
    X,
    ArrowLeft,
    FileText,
    Download,
  } from "lucide-vue-next";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LotDetailModal',
  props: {
    isOpen: { type: Boolean, required: true },
    lot: { type: Object, required: true },
    stage: { type: Object, default: null },
    stageLots: { type: Array, default: () => [] },
    preferredHouseDesignId: { type: String, default: null },
  },
  emits: ["close", "select", "enquiry"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const showLightbox = ref(false);
  const showEnquiryForm = ref(false);
  const selectedDesignIndex = ref(0);

  const enquiryForm = reactive({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const enquiryErrors = reactive({ fullName: "", email: "", phone: "" });

  function resetEnquiryForm() {
    enquiryForm.fullName = "";
    enquiryForm.email = "";
    enquiryForm.phone = "";
    enquiryForm.message = "";
    enquiryErrors.fullName = "";
    enquiryErrors.email = "";
    enquiryErrors.phone = "";
  }

  function validateEnquiry(): boolean {
    let valid = true;

    enquiryErrors.fullName = enquiryForm.fullName.trim()
      ? ""
      : "Full name is required";
    enquiryErrors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enquiryForm.email)
      ? ""
      : "Valid email is required";
    enquiryErrors.phone = enquiryForm.phone.trim() ? "" : "Phone is required";

    if (enquiryErrors.fullName || enquiryErrors.email || enquiryErrors.phone)
      valid = false;

    return valid;
  }

  function submitEnquiry() {
    if (!validateEnquiry()) return;

    emit("enquiry", props.lot, { ...enquiryForm });
    showEnquiryForm.value = false;
    resetEnquiryForm();
  }

  const props = __props;

  const emit = __emit;

  const primaryCtaLabel = computed(() =>
    props.preferredHouseDesignId
      ? `Select Lot ${props.lot?.lotId?.title ?? ""}`.trim()
      : "View Design"
  );

  const activeTab = ref<"details" | "floorplan" | "documents">("details");
  const mobileScrollLocked = ref(false);
  const modalContentReady = ref(false);
  const lockedScrollY = ref(0);
  let previousBodyStyle: Partial<CSSStyleDeclaration> | null = null;
  let modalContentFrameId: number | null = null;

  const cancelModalContentFrame = () => {
    if (modalContentFrameId !== null) {
      cancelAnimationFrame(modalContentFrameId);
      modalContentFrameId = null;
    }
  };

  const scheduleModalContent = () => {
    cancelModalContentFrame();
    modalContentReady.value = false;
    modalContentFrameId = requestAnimationFrame(() => {
      modalContentFrameId = requestAnimationFrame(() => {
        modalContentFrameId = null;
        modalContentReady.value = true;
      });
    });
  };

  const isMobileModalViewport = () =>
    window.matchMedia(
      "(max-width: 680px), (orientation: landscape) and (max-height: 520px) and (max-width: 990px)"
    ).matches;

  const lockMobilePageScroll = () => {
    if (mobileScrollLocked.value || !isMobileModalViewport()) return;

    lockedScrollY.value =
      window.scrollY || document.documentElement.scrollTop || 0;
    previousBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      touchAction: document.body.style.touchAction,
    };

    document.body.classList.add("no-scroll");
    document.body.style.position = "fixed";
    document.body.style.top = `-${lockedScrollY.value}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    mobileScrollLocked.value = true;
  };

  const unlockMobilePageScroll = () => {
    if (!mobileScrollLocked.value) return;

    const scrollY = lockedScrollY.value;

    document.body.classList.remove("no-scroll");
    document.body.style.position = previousBodyStyle?.position ?? "";
    document.body.style.top = previousBodyStyle?.top ?? "";
    document.body.style.left = previousBodyStyle?.left ?? "";
    document.body.style.right = previousBodyStyle?.right ?? "";
    document.body.style.width = previousBodyStyle?.width ?? "";
    document.body.style.overflow = previousBodyStyle?.overflow ?? "";
    document.body.style.touchAction = previousBodyStyle?.touchAction ?? "";
    previousBodyStyle = null;
    mobileScrollLocked.value = false;
    window.scrollTo(0, scrollY);
  };

  const syncMobilePageScrollLock = () => {
    if ((props.isOpen || showLightbox.value) && isMobileModalViewport()) {
      lockMobilePageScroll();
    } else {
      unlockMobilePageScroll();
    }
  };

  const onKeydown = (e: KeyboardEvent) => {
    if (e.code === "Escape" && props.isOpen) emit("close");
  };

  document.addEventListener("keydown", onKeydown);
  onBeforeUnmount(() => {
    cancelModalContentFrame();
    document.removeEventListener("keydown", onKeydown);
    window.removeEventListener("resize", syncMobilePageScrollLock);
    window.removeEventListener("orientationchange", syncMobilePageScrollLock);
    unlockMobilePageScroll();
  });

  watch(
    () => props.isOpen,
    (open) => {
      if (!open) {
        cancelModalContentFrame();
        modalContentReady.value = false;
        showEnquiryForm.value = false;
        resetEnquiryForm();
        return;
      }

      scheduleModalContent();
    },
    { immediate: true }
  );

  watch(() => [props.isOpen, showLightbox.value], syncMobilePageScrollLock, {
    immediate: true,
  });

  onMounted(() => {
    window.addEventListener("resize", syncMobilePageScrollLock);
    window.addEventListener("orientationchange", syncMobilePageScrollLock);
  });

  const lotData = computed(() => props.lot?.lotId ?? {});
  const lotDocuments = computed(() => lotData.value?.documents ?? []);
  const documentCountLabel = computed(() => {
    const count = lotDocuments.value.length;

    return `${count} ${count === 1 ? "document" : "documents"}`;
  });

  const openDocument = (link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const downloadDocument = (document: { link: string; fileName: string }) => {
    const anchor = window.document.createElement("a");

    anchor.href = document.link;
    anchor.download = getDocumentName(document.fileName);
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.click();
  };

  const houseDesigns = computed(() =>
    (lotData.value?.builders?.[0]?.housesIds ?? [])
      .map((h: any) => h.houseId)
      .filter(Boolean)
  );

  const houseDesign = computed(
    () =>
      houseDesigns.value[selectedDesignIndex.value] ??
      houseDesigns.value[0] ??
      null
  );

  const assignedFacade = computed(() => {
    const hd = houseDesign.value;

    if (!hd) return null;

    const houseEntry = (lotData.value?.builders || [])
      .flatMap((b: any) => b.housesIds || [])
      .find((h: any) => String(h.houseId?._id ?? h.houseId) === String(hd._id));
    const assignedIds: string[] = (houseEntry?.facadeIds || []).map(String);

    if (assignedIds.length) {
      const match = (hd.facades || []).find((f: any) =>
        assignedIds.includes(String(f._id))
      );

      if (match) return match;
    }

    return hd.facades?.[0] ?? null;
  });

  const houseImage = computed(
    () =>
      houseDesign.value?.mainImages?.[0] ?? assignedFacade.value?.url ?? null
  );
  const houseDesignName = computed(() => houseDesign.value?.name ?? "");
  const facadeName = computed(() => {
    const hd = houseDesign.value;

    if (!hd) return "";

    const houseEntry = (lotData.value?.builders || [])
      .flatMap((b: any) => b.housesIds || [])
      .find((h: any) => String(h.houseId?._id ?? h.houseId) === String(hd._id));
    const assignedIds: string[] = (houseEntry?.facadeIds || []).map(String);

    if (assignedIds.length === 1) {
      const match = (hd.facades || []).find((f: any) =>
        assignedIds.includes(String(f._id))
      );

      return match?.name ?? "";
    }

    if (assignedIds.length > 1 || (hd.facades || []).length > 1) {
      return "Multiple Facades";
    }

    return hd.facades?.[0]?.name ?? "";
  });

  const displayName = computed(() => houseDesignName.value || "");

  const lotStatusLabel = computed(() =>
    getMasterplanLotStatusLabel(lotData.value?.status)
  );
  const isLotAvailable = computed(
    () =>
      (lotData.value?.status || "").toLowerCase() ===
      LotStatus.AVAILABLE.toLowerCase()
  );

  const stageTitle = computed(() =>
    props.stage?.title ? `${props.stage.title}` : "—"
  );

  const lotPackagePrice = computed(() => {
    return houseDesign.value?.basePrice ?? null;
  });

  const priceDisplay = computed(() => {
    if (!lotPackagePrice.value || lotData.value?.hidePrice) return null;

    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(lotPackagePrice.value);
  });

  const bathroomCount = computed(
    () =>
      houseDesign.value?.bathroom?.quantity ??
      houseDesign.value?.bathroom ??
      null
  );
  const livingCount = computed(
    () =>
      houseDesign.value?.loungeRoom?.quantity ??
      houseDesign.value?.loungeRoom ??
      null
  );

  const getDesignFacadeLabel = (hd: any): string => {
    if (!hd) return "";

    const houseEntry = (lotData.value?.builders || [])
      .flatMap((b: any) => b.housesIds || [])
      .find((h: any) => String(h.houseId?._id ?? h.houseId) === String(hd._id));
    const assignedIds: string[] = (houseEntry?.facadeIds || []).map(String);

    if (assignedIds.length === 1) {
      const match = (hd.facades || []).find((f: any) =>
        assignedIds.includes(String(f._id))
      );

      return match?.name ? `${match.name} Facade` : "";
    }

    if (assignedIds.length > 1 || (hd.facades || []).length > 1) {
      return "Multiple Facades";
    }

    return hd.facades?.[0]?.name ? `${hd.facades[0].name} Facade` : "";
  };

  const getDesignBathroomCount = (design: any) =>
    design?.bathroom?.quantity ?? design?.bathroom ?? null;

  const formatDesignPrice = (design: any): string | null => {
    if (!design?.basePrice) return null;

    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(design.basePrice);
  };

  const hasSpecs = computed(
    () =>
      houseDesign.value?.bedroom ||
      bathroomCount.value ||
      houseDesign.value?.garage ||
      livingCount.value ||
      houseDesign.value?.area
  );

  const floorPlanImage = computed(() => {
    const fp = houseDesign.value?.floorplans?.[0];

    if (!fp) return null;

    return fp?.url ?? (typeof fp === "string" ? fp : null);
  });

  const preferredDesignIndex = () => {
    if (!props.preferredHouseDesignId) return 0;

    const idx = houseDesigns.value.findIndex(
      (d: any) => String(d._id) === String(props.preferredHouseDesignId)
    );

    return idx >= 0 ? idx : 0;
  };

  watch(
    () => props.lot,
    () => {
      activeTab.value = "details";
      showEnquiryForm.value = false;
      resetEnquiryForm();
      selectedDesignIndex.value = preferredDesignIndex();
    },
    { immediate: true }
  );
  watch(
    () => props.preferredHouseDesignId,
    () => {
      selectedDesignIndex.value = preferredDesignIndex();
    }
  );

  const validStageLots = computed(() => {
    return (props.stageLots as any[]).filter((l: any) => l.boundingBox);
  });

  const miniMapViewBox = computed(() => {
    if (!validStageLots.value.length) return "";

    const pad = 8;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (const l of validStageLots.value) {
      const bb = l.boundingBox;

      if (bb.x < minX) minX = bb.x;

      if (bb.y < minY) minY = bb.y;

      if (bb.x + bb.width > maxX) maxX = bb.x + bb.width;

      if (bb.y + bb.height > maxY) maxY = bb.y + bb.height;
    }

    return `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${
      maxY - minY + pad * 2
    }`;
  });

  const lotShapeViewBox = computed(() => {
    const bb = props.lot?.boundingBox;

    if (!bb) return "0 0 100 100";

    const pad = Math.min(bb.width, bb.height) * 0.25;

    return `${bb.x - pad} ${bb.y - pad} ${bb.width + pad * 2} ${
      bb.height + pad * 2
    }`;
  });

  const lotShapeFontSize = computed(() => {
    const bb = props.lot?.boundingBox;

    if (!bb) return 12;

    return Math.max(8, Math.round(Math.min(bb.width, bb.height) * 0.35));
  });

  const lotShapeTextCenter = computed(() => {
    const lot = props.lot;

    if (lot?.polygonPoints && lot.polygonPoints.length > 0) {
      return calculatePolygonCentroid(lot.polygonPoints);
    }

    const bb = lot?.boundingBox;

    if (bb) return { x: bb.x + bb.width / 2, y: bb.y + bb.height / 2 };

    return { x: 0, y: 0 };
  });

  const getMiniLotClass = (l: any) => {
    const isSelected = l.lotId?._id && l.lotId._id === lotData.value?._id;
    const status = l.lotId?.status || "Not Released";

    return [
      "mini-lot-rect",
      getMasterplanLotStatusClass(status),
      { "mini-lot-selected": isSelected },
    ];
  };

const __returned__ = { showLightbox, showEnquiryForm, selectedDesignIndex, enquiryForm, enquiryErrors, resetEnquiryForm, validateEnquiry, submitEnquiry, props, emit, primaryCtaLabel, activeTab, mobileScrollLocked, modalContentReady, lockedScrollY, get previousBodyStyle() { return previousBodyStyle }, set previousBodyStyle(v) { previousBodyStyle = v }, get modalContentFrameId() { return modalContentFrameId }, set modalContentFrameId(v) { modalContentFrameId = v }, cancelModalContentFrame, scheduleModalContent, isMobileModalViewport, lockMobilePageScroll, unlockMobilePageScroll, syncMobilePageScrollLock, onKeydown, lotData, lotDocuments, documentCountLabel, openDocument, downloadDocument, houseDesigns, houseDesign, assignedFacade, houseImage, houseDesignName, facadeName, displayName, lotStatusLabel, isLotAvailable, stageTitle, lotPackagePrice, priceDisplay, bathroomCount, livingCount, getDesignFacadeLabel, getDesignBathroomCount, formatDesignPrice, hasSpecs, floorPlanImage, preferredDesignIndex, validStageLots, miniMapViewBox, lotShapeViewBox, lotShapeFontSize, lotShapeTextCenter, getMiniLotClass, computed, ref, reactive, onBeforeUnmount, onMounted, watch, get getMasterplanLotStatusClass() { return getMasterplanLotStatusClass }, get getMasterplanLotStatusLabel() { return getMasterplanLotStatusLabel }, get getMasterplanStatusBadgeStyle() { return getMasterplanStatusBadgeStyle }, get calculatePolygonCentroid() { return calculatePolygonCentroid }, get formatDate() { return formatDate }, get getDocumentName() { return getDocumentName }, get LotStatus() { return LotStatus }, get Bed() { return Bed }, get Bath() { return Bath }, get Car() { return Car }, get Sofa() { return Sofa }, get Maximize2() { return Maximize2 }, get MoveHorizontal() { return MoveHorizontal }, get Send() { return Send }, get Layers() { return Layers }, get Navigation() { return Navigation }, get X() { return X }, get ArrowLeft() { return ArrowLeft }, get FileText() { return FileText }, get Download() { return Download } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})